import { useEffect, useMemo, useState } from 'react'
import { FeatureDemo } from 'components/feature-demo/FeatureDemo'
import {
  featureDemoStorageId,
  shouldShowFeatureDemo,
  writeFeatureDemoDismissed,
  type FeatureDemoChoice,
} from 'components/feature-demo/featureDemoStorage'
import { COMMAND_PALETTE_DEMO_ID } from 'components/command-palette/CommandPaletteOnboarding'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useAuth } from 'hooks/auth/useAuth'
import { ASSISTANT_DEMO_EVENT, PORTAL_ASSISTANT_DEMO_ID, type AssistantDemoDetail } from './PortalAssistantOnboarding'

export const PORTAL_ASSISTANT_TOOLS_DEMO_ID = 'portal-assistant-tools-tour'
export const COLTON_TOOLS_DEMO_EMAIL = 'colton@optictruckworks.com'

export function isColtonToolsDemoUser(email: string | null | undefined): boolean {
  return (email || '').trim().toLowerCase() === COLTON_TOOLS_DEMO_EMAIL
}

type ToolsDemoAnchor = 'tools' | 'settings'

const INTRO = {
  title: 'Tools and charts',
  body: 'Show Me walks through the wrench, saving a new tool, and the chart switches. The description stays on screen while the tool list scrolls. Save always drafts and formats the tool with AI.',
}

const DEMO_STEPS: ReadonlyArray<{
  title: string
  body: string
  anchor: ToolsDemoAnchor
}> = [
  {
    title: 'The wrench keeps the description in view',
    body: 'This opens the tool list. The list scrolls on the left. The description, the on/off switch, and who edited it last stay on the right. Schema scrolls underneath that, so you do not lose the description.',
    anchor: 'tools',
  },
  {
    title: 'Save drafts a proper tool',
    body: 'New tool asks what you want in plain language. Save always drafts the name, description, and schema with AI, using the tools already in the portal and the data those tools read: inventory, leads, applications, expenses, repairs, shipping, and Gray Book. It is stored only when that result is a proper tool. Saving an existing tool formats it the same way. The next reply can call the skill. The description tells the assistant which existing lookups to use. It does not add a new database query.',
    anchor: 'tools',
  },
  {
    title: 'Charts pick one, then offer the rest',
    body: 'Open the gear and look under Tools. Each switch is a chart type: line, bar, pie, scatter, radar, funnel, heatmap, gauge, and sparkline. Turn on the ones you want. The assistant picks one and draws it. Under the chart, Visualize this in a lists the others you turned on. Those buttons switch the same numbers.',
    anchor: 'settings',
  },
]

interface PortalAssistantToolsOnboardingProps {
  anchorEl: HTMLElement | null
  isMobile?: boolean
  defer?: boolean
  onOpenAssistant: () => void
}

export function PortalAssistantToolsOnboarding({
  anchorEl,
  isMobile = false,
  defer = false,
  onOpenAssistant,
}: PortalAssistantToolsOnboardingProps) {
  const { currentUser, userInfo } = useAuth()
  const uid = currentUser?.uid || userInfo?.id
  const isColton = isColtonToolsDemoUser(userInfo?.email)
  const enabled = FEATURE_FLAGS.portalAssistantToolsOnboarding && isColton && !defer
  const storageId = featureDemoStorageId(PORTAL_ASSISTANT_TOOLS_DEMO_ID, uid)
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)
  const [demoIndex, setDemoIndex] = useState<number | null>(null)
  const [stepAnchor, setStepAnchor] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 400)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    if (!ready || !anchorEl || !uid || !isColton) return undefined
    const tick = () => {
      const palettePending = shouldShowFeatureDemo(
        featureDemoStorageId(COMMAND_PALETTE_DEMO_ID, uid),
        { enabled: FEATURE_FLAGS.commandPaletteOnboarding },
      )
      const assistantPending = shouldShowFeatureDemo(
        featureDemoStorageId(PORTAL_ASSISTANT_DEMO_ID, uid),
        { enabled: FEATURE_FLAGS.portalAssistantOnboarding },
      )
      setVisible(
        shouldShowFeatureDemo(storageId, {
          enabled: enabled && !palettePending && !assistantPending,
        }),
      )
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [ready, anchorEl, uid, isColton, enabled, storageId])

  const step = demoIndex === null ? null : DEMO_STEPS[demoIndex]
  const stepLabel = useMemo(() => {
    if (demoIndex === null) return 'New'
    return `Step ${demoIndex + 1} of ${DEMO_STEPS.length}`
  }, [demoIndex])

  useEffect(() => {
    if (!step) return undefined
    onOpenAssistant()
    window.dispatchEvent(
      new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail: { active: true } }),
    )
    let attempts = 0
    let timer = 0
    const look = () => {
      const exact = document.querySelector(`[data-assistant-demo="${step.anchor}"]`)
      if (exact instanceof HTMLElement) {
        exact.scrollIntoView({ block: 'nearest' })
        setStepAnchor(exact)
        return
      }
      attempts += 1
      if (attempts < 8) {
        timer = window.setTimeout(look, 120)
      }
    }
    timer = window.setTimeout(look, 80)
    return () => window.clearTimeout(timer)
  }, [step, onOpenAssistant])

  const dismiss = (choice: FeatureDemoChoice) => {
    writeFeatureDemoDismissed(storageId, choice)
    setVisible(false)
    setDemoIndex(null)
    setStepAnchor(null)
    window.dispatchEvent(
      new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail: { active: false } }),
    )
  }

  if (!visible || !anchorEl || !isColton) return null

  const popperAnchor = step && stepAnchor ? stepAnchor : anchorEl
  const placement = isMobile ? 'bottom' : 'left-start'

  if (step && demoIndex !== null) {
    const isLast = demoIndex === DEMO_STEPS.length - 1
    return (
      <FeatureDemo
        open
        anchorEl={popperAnchor}
        title={step.title}
        body={step.body}
        stepLabel={stepLabel}
        primaryLabel={isLast ? 'Done' : 'Next'}
        secondaryLabel={demoIndex > 0 ? 'Back' : undefined}
        tertiaryLabel='Skip'
        onPrimary={() => {
          if (isLast) dismiss('completed')
          else setDemoIndex(index => (index ?? 0) + 1)
        }}
        onSecondary={demoIndex > 0 ? () => setDemoIndex(index => (index ?? 1) - 1) : undefined}
        onTertiary={() => dismiss('skipped')}
        placement={placement}
      />
    )
  }

  return (
    <FeatureDemo
      open
      anchorEl={anchorEl}
      title={INTRO.title}
      body={INTRO.body}
      stepLabel='New'
      primaryLabel='Show Me'
      secondaryLabel='Got It'
      onPrimary={() => setDemoIndex(0)}
      onSecondary={() => dismiss('skipped')}
      placement={isMobile ? 'bottom' : 'bottom-end'}
    />
  )
}
